import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { RestApiUipathService } from 'app/rpa-bots/uipath/rest-api/rest-api-uipath.service'
import {
  EUIPathDashboardActions,
  getUIPathJobsList,
  getUIPathJobsListSuccess,
  getUIPathQueueitemsList,
  getUIPathQueueitemsListSuccess,
  UIPathRetryJob,
  UIPathRetryQueueitem,
  UIPathRetryQueueitemSuccess,
  getUIPathJobsData,
  getUIPathJobsDataSuccess,
  getUIPathQueueitemsData,
  getUIPathQueueitemsDataSuccess,
  UIPathJobRetrySuccess,
  UIPathResetJobRetry,
  UIPathResetQueueitemRetry,
  UIPathErrorHandler
} from 'app/rpa-bots/uipath/store/actions/rpa-uipath-dashboard.actions';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthType, ConstAuthClientCredentials, UipathConnectionType } from '../../common/models/enums';
import { IDataCharts } from '../../common/models/IDataCharts';
import { IQueueItem } from '../../common/models/IQueueItem';
import { IUipathJob } from '../../common/models/IUipathJob';

@Injectable()
export class UIPathDashboardEffects {

  getUIPathJobList$ = createEffect(() =>
    this._actions$.pipe(
      ofType<getUIPathJobsList>(EUIPathDashboardActions.getUIPathJobsList),
      switchMap((param) => {
        return this._rpaUiPathService.getToken(param.payload.connection)
          .pipe(
            catchError(err => {
              return 'ERROR'
            })
          ).switchMap((token) => {
            if (token === 'ERROR')
              return 'ERROR';
            let connection = param.payload.connection;
            if (connection.connectionType == UipathConnectionType.cloud
              && connection.authType == AuthType.clientCredentials) {
              var organizationUnits: string[] = Array.from(new Set(connection.processes.filter(f => f.organizationUnit).map(p => p.organizationUnit)));
              return this._rpaUiPathService.getJobsCC(param.payload.connection.connectionType, param.payload.filter,
                token, param.payload.connection.server, param.payload.connection.userKey,
                param.payload.connection.accountLogicalName, param.payload.connection.tenantLogicalName,
                param.payload.connection.clientId, organizationUnits)
                .pipe(
                  catchError(err => {
                    return 'ERROR'
                  })
                );
            }
            return this._rpaUiPathService.getJobs(param.payload.connection.connectionType, param.payload.filter,
              token, param.payload.connection.server, param.payload.connection.userKey,
              param.payload.connection.accountLogicalName, param.payload.connection.tenantLogicalName,
              param.payload.connection.clientId)
              .pipe(
                catchError(err => {
                  return 'ERROR'
                })
              );
          })
      }),
      switchMap((jobs: IODataResponse<IUipathJob[]> | string) =>
        of(typeof jobs !== 'string' ? new getUIPathJobsListSuccess(jobs) : new UIPathErrorHandler()))
    )
  );
  getUIPathQueueitemsList$ = createEffect(() =>
    this._actions$.pipe(
      ofType<getUIPathQueueitemsList>(EUIPathDashboardActions.getUIPathQueueitemsList),
      switchMap((param) => {
        return this._rpaUiPathService.getToken(param.payload.connection).pipe(
          catchError(err => {
            return 'ERROR'
          })
        ).switchMap((token) => {
          if (token === 'ERROR')
            return 'ERROR';
          let connection = param.payload.connection;
          if (connection.connectionType == UipathConnectionType.cloud
            && connection.authType == AuthType.clientCredentials) {
            var organizationUnits: string[] = Array.from(new Set(connection.processes.filter(f => f.organizationUnit).map(p => p.organizationUnit)));
            return this._rpaUiPathService.getQueueItemsCC(param.payload.connection.connectionType,
              param.payload.filter, token, param.payload.connection.server,
              param.payload.connection.userKey, param.payload.connection.accountLogicalName,
              param.payload.connection.tenantLogicalName, param.payload.connection.clientId,
              organizationUnits)
              .pipe(
                catchError(err => {
                  return 'ERROR'
                })
              );
          }
          return this._rpaUiPathService.getQueueItems(param.payload.connection.connectionType,
            param.payload.filter, token, param.payload.connection.server,
            param.payload.connection.userKey, param.payload.connection.accountLogicalName,
            param.payload.connection.tenantLogicalName, param.payload.connection.clientId)
            .pipe(
              catchError(err => {
                return 'ERROR'
              })
            );
        })
      }),
      switchMap((queueitems: IODataResponse<IQueueItem[]> | string) =>
        of(typeof queueitems !== 'string' ? new getUIPathQueueitemsListSuccess(queueitems) : new UIPathErrorHandler()))
    )
  );
  getUIPathJobsData$ = createEffect(() =>
    this._actions$.pipe(
      ofType<getUIPathJobsData>(EUIPathDashboardActions.getUIPathJobsData),
      switchMap((param) => {
        return this._rpaUiPathService.getToken(param.payload).pipe(
          switchMap((token) => {
            if (token === 'ERROR') {
              return of('ERROR');
            }
            // Validate authentication type, it will consume a different service
            if (param.payload.connectionType == UipathConnectionType.cloud
              && param.payload.authType == AuthType.clientCredentials) {
              const organizationUnits: string[] = Array.from(new Set(param.payload.processes.filter(f => f.organizationUnit).map(p => p.organizationUnit)));
              return this._rpaUiPathService.getJobsDataChartsCC(
                param.payload.connectionType, token,
                param.payload.server, param.payload.userKey, param.payload.accountLogicalName,
                param.payload.tenantLogicalName, param.payload.clientId, organizationUnits
              ).pipe(
                catchError(err => of('ERROR'))
              );
            }
            return this._rpaUiPathService.getJobsDataCharts(
              param.payload.connectionType, token,
              param.payload.server, param.payload.userKey,
              param.payload.accountLogicalName, param.payload.tenantLogicalName, param.payload.clientId
            ).pipe(
              catchError(err => of('ERROR'))
            );
          }),
          catchError(err => of('ERROR'))
        );
      }),
      switchMap((dataChartJobs: IDataCharts | string) =>
        of(typeof dataChartJobs !== 'string' ? new getUIPathJobsDataSuccess(dataChartJobs) : new UIPathErrorHandler())
      )
    )
  );
  UiPathRetryJob$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UIPathRetryJob>(EUIPathDashboardActions.UIPathRetryJob),
      switchMap((param) => {
        return this._rpaUiPathService.getToken(param.payload.connection).pipe(
          catchError(err => {
            return 'ERROR'
          })
        ).switchMap((token) => {
          if (token === 'ERROR')
            return of(new UIPathResetJobRetry());
          return this._rpaUiPathService.getReleaseKey(param.payload.connection.connectionType, token,
            param.payload.jobKey, param.payload.connection.server, param.payload.connection.userKey,
            param.payload.connection.accountLogicalName, param.payload.connection.tenantLogicalName,
            param.payload.connection.clientId)
            .pipe(
              catchError(err => {
                return 'ERROR'
              })
            ).switchMap((releaseKey) => {
              if (releaseKey === 'ERROR')
                return of(new UIPathResetJobRetry());
              return this._rpaUiPathService.startJob(param.payload.connection.connectionType, token, releaseKey,
                param.payload.inputArguments, param.payload.connection.server, param.payload.connection.userKey,
                param.payload.connection.accountLogicalName, param.payload.connection.tenantLogicalName,
                param.payload.connection.clientId).pipe(
                  catchError(err => {
                    return of(new UIPathResetJobRetry());
                  })
                )
            })
        })
      }),
      switchMap((data: any) =>
        of((data && data.type === EUIPathDashboardActions.UIPathResetJobRetry) ? new UIPathErrorHandler() : new UIPathJobRetrySuccess(data.value[0].Id))
      ))
  );
  getUIPathQueueitemsData$ = createEffect(() =>
    this._actions$.pipe(
      ofType<getUIPathQueueitemsData>(EUIPathDashboardActions.getUIPathQueueitemsData),
      switchMap((param) => {
        return this._rpaUiPathService.getToken(param.payload).pipe(
          switchMap((token) => {
            if (token === 'ERROR') {
              return of('ERROR');
            }
            // Validate authentication type, it will consume a different service
            if (param.payload.connectionType == UipathConnectionType.cloud
              && param.payload.authType == AuthType.clientCredentials) {
              const organizationUnits: string[] = Array.from(new Set(param.payload.processes.filter(f => f.organizationUnit).map(p => p.organizationUnit)));
              return this._rpaUiPathService.getQueueitemsDataChartsCC(
                param.payload.connectionType, token,
                param.payload.server, param.payload.userKey, param.payload.accountLogicalName,
                param.payload.tenantLogicalName, param.payload.clientId, organizationUnits
              ).pipe(
                catchError(err => of('ERROR'))
              );
            }
            return this._rpaUiPathService.getQueueitemsDataCharts(
              param.payload.connectionType, token,
              param.payload.server, param.payload.userKey,
              param.payload.accountLogicalName, param.payload.tenantLogicalName, param.payload.clientId
            ).pipe(
              catchError(err => of('ERROR'))
            );
          }),
          catchError(err => of('ERROR'))
        );
      }),
      switchMap((dataChartQueueitems: IDataCharts | string) =>
        of(typeof dataChartQueueitems !== 'string' ? new getUIPathQueueitemsDataSuccess(dataChartQueueitems) : new UIPathErrorHandler())
      )
    )
  );
  UIPathRetryQueueitem$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UIPathRetryQueueitem>(EUIPathDashboardActions.UIPathRetryQueueitem),
      switchMap((param) => {
        return this._rpaUiPathService.getToken(param.payload.connection)
          .pipe(
            catchError(err => {
              return 'ERROR'
            })
          ).switchMap((token) => {
            if (token === 'ERROR')
              return 'ERROR'
            return this._rpaUiPathService.retryQueueItem(param.payload.connection.connectionType, token,
              param.payload.connection.queueid, param.payload.connection.server, param.payload.connection.userKey,
              param.payload.connection.accountLogicalName, param.payload.connection.tenantLogicalName,
              param.payload.connection.clientId).pipe(
                catchError(err => {
                  return 'ERROR'
                })
              )
          })
      }),
      switchMap((dataNewQueueitem: any) =>
        of(typeof dataNewQueueitem !== 'string' ? new UIPathRetryQueueitemSuccess(dataNewQueueitem.Id) : new UIPathErrorHandler()))
    )
  );

  constructor(
    private _rpaUiPathService: RestApiUipathService,
    private _actions$: Actions
  ) {
  }
}
